import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import "./KPIDashboardScreen.css";
import "./CrewPilotSharedForm.css";
import { CrewPilotModuleShell } from "./CrewPilotModuleShell";
import { CPInput } from "./CPInput";
import { CPDropdown } from "./CPDropdown";
import { CPTextarea } from "./CPTextarea";
import { supabase } from "../supabase";
import {
  insertScopedProject,
  loadScopedActiveProjects,
  loadScopedClients,
  updateScopedProject,
} from "../helpers/operationalQueryAdapters";
import {
  assertClientOperationallyWritable,
  isClientOperationallyLocked,
  OPERATIONAL_LOCK_CLIENT_MESSAGE,
} from "../helpers/operationalLifecycle";
import { isClientArchived } from "../helpers/clientVisibility";
import { blockDemoSave, readGuestSession, shouldSkipSupabase } from "../helpers/guestMode";
import { loadFieldProjects } from "../helpers/projectFieldSnapshot";
import {
  partitionFavoriteHubProjects,
  readFavoriteProjectIds,
  readFavoriteReadyMap,
  retryFavoriteProject,
  toggleProjectFavorite,
} from "../helpers/projectFavorites";
import { markEstimateConverted } from "../helpers/estimates";
import { isAdmin } from "../helpers/permissions";
import { getProjectsRelationName } from "../helpers/operationalProjections";
import { ensureProjectQuoteSnapshotsFromEstimate } from "../helpers/projectQuoteSnapshots";
import { copyEstimateStructureToProject } from "../helpers/projectStructure";
import { useHardwareBackHandler } from "../navigation/HardwareBackContext";
import { useDashboardCarouselSwipe } from "../hooks/useDashboardCarouselSwipe";
import {
  PROJECT_HUB_OVERDUE_TASKS,
  PROJECT_HUB_STATUS_FILTERS,
  projectMatchesHubFilter,
  resolveProjectHubFilter,
} from "../helpers/projectHubFilters";
import {
  archiveProject,
  getActiveProjects,
  notifyProjectArchived,
  restoreProject,
} from "../helpers/projectVisibility";
import {
  VoiceGuidedFormProvider,
  useVoiceGuidedForm,
  useGuidedFieldHandlers,
} from "../ashley/forms/VoiceGuidedFormContext";
import { VoiceGuidedFormOverlay } from "../ashley/forms/VoiceGuidedFormOverlay";
import { useCreateProjectVoiceMvp } from "../ashley/createProject/useCreateProjectVoiceMvp";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITY } from "../helpers/permissions";
import {
  DEFAULT_PROJECT_HUB_SORT,
  PROJECT_HUB_SORT_OPTIONS,
  sortProjectHubList,
} from "../helpers/projectHubSort";
import { PAGE_LAYOUT } from "../layout/pageLayout";
import "./project/projects.css";
import "./project/projectForm.css";
import {
  RecBar,
  useKeepFocusedFieldVisible,
  useKeyboardOpen,
} from "./materials/MaterialRecordParts";
import { ProjectCard } from "./project/ProjectCard";
import { DesktopKpiStrip } from "./shared/DesktopKpiStrip";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { useProjectTaskStats } from "../hooks/useProjectTaskStats";
import {
  TimeLogsActionBar,
  TimeLogsActiveFilters,
  TimeLogsFilterSheet,
  TimeLogsScopeTabs,
  TimeLogsSearchRow,
} from "./timelogs/TimeLogsToolbar";
import {
  PROJECT_CREATION_IDEMPOTENCY_COLUMN,
  PROJECT_SOURCE_ESTIMATE_COLUMN,
  classifyProjectUniqueViolation,
  createInFlightSubmissionLock,
  createProjectCreationSession,
} from "../helpers/projectCreationIdempotency";
import { isUuidString } from "../helpers/uuidFields";

const PROJECT_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "on_hold", label: "On Hold" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
];

/**
 * Console-only diagnostics. The alert stays generic so schema and policy
 * text never reaches the user.
 */
function logProjectMutationFailure(action, error) {
  console.error(
    `[${action}] Supabase error:`,
    error?.code || "",
    error?.message || error,
    error?.details || "",
    error?.hint || "",
  );
}

function buildProjectNotes(notes, phone, email) {
  const extras = [];

  if (phone?.trim()) {
    extras.push(`Contact phone: ${phone.trim()}`);
  }

  if (email?.trim()) {
    extras.push(`Contact email: ${email.trim()}`);
  }

  if (!extras.length) {
    return notes;
  }

  return [notes, ...extras].filter(Boolean).join("\n");
}

const PF_ICONS = {
  doc: <path d="M7 3.5h7l4 4V19a1.5 1.5 0 0 1-1.5 1.5h-9.5A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5ZM13.5 3.5V8H18M8.5 12h7M8.5 15.5h7" />,
  pin: (
    <>
      <path d="M12 21s6.5-5.6 6.5-11a6.5 6.5 0 0 0-13 0c0 5.4 6.5 11 6.5 11Z" />
      <circle cx="12" cy="10" r="2.3" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="8.5" r="3" />
      <path d="M3.5 19c.4-3 2.7-4.8 5.5-4.8s5.1 1.8 5.5 4.8M16 6a2.7 2.7 0 0 1 0 5.3M17.5 14.5c1.9.5 3 2 3.3 4.5" />
    </>
  ),
  notes: <path d="M6 4h12a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1ZM8.5 9h7M8.5 12.5h7M8.5 16h4" />,
};

function PfIcon({ name }) {
  return (
    <svg
      className="pf-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PF_ICONS[name]}
    </svg>
  );
}


function CreateProjectFormFields({
  projectName,
  setProjectName,
  projectNumber,
  setProjectNumber,
  client,
  setClient,
  clientOptions,
  clientDropdownRef,
  siteAddress,
  setSiteAddress,
  projectType,
  setProjectType,
  clientContactName,
  setClientContactName,
  clientContactPhone,
  setClientContactPhone,
  clientContactEmail,
  setClientContactEmail,
  startDate,
  setStartDate,
  estimatedEndDate,
  setEstimatedEndDate,
  projectManager,
  setProjectManager,
  status,
  setStatus,
  budgetAmount,
  setBudgetAmount,
  hourlyRate,
  setHourlyRate,
  notes,
  setNotes,
  onCancel,
  onSubmit,
  submitting = false,
  title = "Create Project",
  subtitle = "",
  isEditing = false,
}) {
  useKeyboardOpen(true);
  useKeepFocusedFieldVisible(true);
  const guidedForm = useVoiceGuidedForm();
  const voiceActive = (fieldId) => guidedForm?.activeFieldId === fieldId;

  const clientHandlers = useGuidedFieldHandlers("client", setClient);
  const projectNameHandlers = useGuidedFieldHandlers("projectName", setProjectName);
  const projectNumberHandlers = useGuidedFieldHandlers("projectNumber", setProjectNumber);
  const siteAddressHandlers = useGuidedFieldHandlers("siteAddress", setSiteAddress);
  const projectTypeHandlers = useGuidedFieldHandlers("projectType", setProjectType);
  const clientContactNameHandlers = useGuidedFieldHandlers(
    "clientContactName",
    setClientContactName,
  );
  const clientContactPhoneHandlers = useGuidedFieldHandlers(
    "clientContactPhone",
    setClientContactPhone,
  );
  const clientContactEmailHandlers = useGuidedFieldHandlers(
    "clientContactEmail",
    setClientContactEmail,
  );
  const startDateHandlers = useGuidedFieldHandlers("startDate", setStartDate);
  const estimatedEndDateHandlers = useGuidedFieldHandlers(
    "estimatedEndDate",
    setEstimatedEndDate,
  );
  const projectManagerHandlers = useGuidedFieldHandlers(
    "projectManager",
    setProjectManager,
  );
  const statusHandlers = useGuidedFieldHandlers("status", setStatus);
  const budgetAmountHandlers = useGuidedFieldHandlers("budgetAmount", setBudgetAmount);
  const hourlyRateHandlers = useGuidedFieldHandlers("hourlyRate", setHourlyRate);
  const notesHandlers = useGuidedFieldHandlers("notes", setNotes);

  return (
    <div className="screen nx-screen rec-page rec-page--edit pf-page" data-testid="project-form">
      <RecBar title={title} leading="back" onBack={onCancel} />
      {subtitle ? <div className="pf-subtitle">{subtitle}</div> : null}

      <div className="rec-body pf-form cp-form-compact">
        <section className="rec-card pf-card" aria-label="Project information">
          <div className="pf-head"><PfIcon name="doc" /><span>Project Information</span></div>
          <CPInput
            label="Project Name"
            voiceFieldId="projectName"
            voiceActive={voiceActive("projectName")}
            value={projectName}
            onChange={projectNameHandlers.onChange}
            onBlur={projectNameHandlers.onBlur}
            placeholder="Enter project name"
            required
          />

          <div className="pf-row pf-row--client">
            <CPInput
              label="Project Number"
              voiceFieldId="projectNumber"
              voiceActive={voiceActive("projectNumber")}
              value={projectNumber}
              onChange={projectNumberHandlers.onChange}
              onBlur={projectNumberHandlers.onBlur}
              placeholder="Optional"
            />

            <CPDropdown
              ref={clientDropdownRef}
              label="Client"
              voiceFieldId="client"
              voiceActive={voiceActive("client")}
              value={client}
              onChange={clientHandlers.onChange}
              placeholder="Select client"
              options={clientOptions}
              required
            />
          </div>
        </section>

        <section className="rec-card pf-card" aria-label="Site and schedule">
          <div className="pf-head"><PfIcon name="pin" /><span>Site &amp; Schedule</span></div>
          <CPInput
            label="Site Address"
            voiceFieldId="siteAddress"
            voiceActive={voiceActive("siteAddress")}
            value={siteAddress}
            onChange={siteAddressHandlers.onChange}
            onBlur={siteAddressHandlers.onBlur}
            placeholder="Enter site address"
          />

          <div className="cp-row-grid-2">
            <CPInput
              label="Project Type"
              voiceFieldId="projectType"
              voiceActive={voiceActive("projectType")}
              value={projectType}
              onChange={projectTypeHandlers.onChange}
              onBlur={projectTypeHandlers.onBlur}
              placeholder="e.g. Residential"
            />

            <CPDropdown
              label="Status"
              voiceFieldId="status"
              voiceActive={voiceActive("status")}
              value={status}
              onChange={statusHandlers.onChange}
              options={PROJECT_STATUS_OPTIONS}
            />
          </div>

          <div className="cp-row-grid-2">
            <CPInput
              label="Start Date"
              type="date"
              voiceFieldId="startDate"
              voiceActive={voiceActive("startDate")}
              value={startDate}
              onChange={startDateHandlers.onChange}
              onBlur={startDateHandlers.onBlur}
            />

            <CPInput
              label="Target Completion"
              type="date"
              voiceFieldId="estimatedEndDate"
              voiceActive={voiceActive("estimatedEndDate")}
              value={estimatedEndDate}
              onChange={estimatedEndDateHandlers.onChange}
              onBlur={estimatedEndDateHandlers.onBlur}
            />
          </div>
          <div className="pf-help">Target Completion is an internal planning date. It is not shown to your client.</div>
        </section>

        <section className="rec-card pf-card" aria-label="Contacts and management">
          <div className="pf-head"><PfIcon name="people" /><span>Contacts &amp; Management</span></div>
          <CPInput
            label="Primary Contact"
            voiceFieldId="clientContactName"
            voiceActive={voiceActive("clientContactName")}
            value={clientContactName}
            onChange={clientContactNameHandlers.onChange}
            onBlur={clientContactNameHandlers.onBlur}
            placeholder="Enter primary contact name"
          />

          <div
            data-voice-field="contactMethod"
            className={`cp-row-grid-2 ${
              voiceActive("contactMethod") ? "cp-voice-field-group--active" : ""
            }`}
          >
            <CPInput
              label="Phone"
              voiceFieldId="clientContactPhone"
              voiceActive={voiceActive("clientContactPhone")}
              value={clientContactPhone}
              onChange={clientContactPhoneHandlers.onChange}
              onBlur={clientContactPhoneHandlers.onBlur}
              placeholder="Phone number"
              type="tel"
            />

            <CPInput
              label="Email"
              voiceFieldId="clientContactEmail"
              voiceActive={voiceActive("clientContactEmail")}
              value={clientContactEmail}
              onChange={clientContactEmailHandlers.onChange}
              onBlur={clientContactEmailHandlers.onBlur}
              placeholder="Email address"
              type="email"
            />
          </div>

          <div className="pf-row pf-row--mgmt">
            <CPInput
              label="Project Manager"
              voiceFieldId="projectManager"
              voiceActive={voiceActive("projectManager")}
              value={projectManager}
              onChange={projectManagerHandlers.onChange}
              onBlur={projectManagerHandlers.onBlur}
              placeholder="Enter project manager"
            />

            <CPInput
              label="Budget Amount"
              voiceFieldId="budgetAmount"
              voiceActive={voiceActive("budgetAmount")}
              value={budgetAmount}
              onChange={budgetAmountHandlers.onChange}
              onBlur={budgetAmountHandlers.onBlur}
              placeholder="Project budget"
              inputMode="decimal"
            />

            <CPInput
              label="Hourly Rate"
              voiceFieldId="hourlyRate"
              voiceActive={voiceActive("hourlyRate")}
              value={hourlyRate}
              onChange={hourlyRateHandlers.onChange}
              onBlur={hourlyRateHandlers.onBlur}
              placeholder="Hourly rate"
              inputMode="decimal"
            />
          </div>
        </section>

        <section className="rec-card pf-card pf-card--notes" aria-label="Notes">
          <div className="pf-head"><PfIcon name="notes" /><span>Notes</span></div>
          <CPTextarea
            label="Notes"
            voiceFieldId="notes"
            voiceActive={voiceActive("notes")}
            value={notes}
            onChange={notesHandlers.onChange}
            onBlur={notesHandlers.onBlur}
            placeholder="Project notes, special instructions, scope details..."
            rows={2}
          />
        </section>
      </div>

      <div className="rec-actions rec-actions--split">
        <button type="button" className="rec-btn rec-btn--ghost" onClick={onCancel}>
          Cancel
        </button>

        <button
          className="rec-btn rec-btn--primary"
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          aria-busy={submitting}
        >
          {submitting ? "Saving…" : isEditing ? "Save Changes" : "Create Project"}
        </button>
      </div>
    </div>
  );
}


function formatProjectValue(amount) {
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `$${Math.round(amount)}`;
  }
}

function getProjectClientName(project) {
  return project.clients?.client_name || project.client_name || "";
}

function projectMatchesSearch(project, search) {
  if (!search) return true;

  const clientName = getProjectClientName(project);

  return (
    project.project_name?.toLowerCase().includes(search) ||
    clientName.toLowerCase().includes(search) ||
    project.project_number?.toLowerCase().includes(search)
  );
}

export function resolveCreateProjectInitialView({
  editProjectId,
  fromEstimate,
  initialView = "hub",
}) {
  if (editProjectId) {
    return "form";
  }
  if (fromEstimate?.estimateId) {
    return "form";
  }
  return initialView;
}

export function CreateProjectScreen({
  onBack,
  currentUser,
  onOpenProjectDetail,
  editProjectId,
  onEditFinished,
  fromEstimate = null,
  initialView = "hub",
  initialClientId = "",
  initialHubFilter = null,
  voiceWorkflow = null,
  onVoiceWorkflowConsumed,
  onCarouselNavigate,
  onOpenNavigator,
}) {
  const { can, actor } = usePermissions();
  const showFinancial = can(CAPABILITY.PROJECT_DETAIL_FINANCIAL);
  const touchStartX = useRef(null);
  const clientDropdownRef = useRef(null);
  const isFromEstimate = Boolean(fromEstimate?.estimateId);
  const sourceEstimateId = fromEstimate?.estimateId || "";
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionLockRef = useRef(null);
  const creationSessionRef = useRef(null);
  if (submissionLockRef.current == null) {
    submissionLockRef.current = createInFlightSubmissionLock();
  }
  if (creationSessionRef.current == null) {
    creationSessionRef.current = createProjectCreationSession();
  }
  const [view, setView] = useState(() =>
    resolveCreateProjectInitialView({ editProjectId, fromEstimate, initialView }),
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [hubStatusFilter, setHubStatusFilter] = useState(() =>
    resolveProjectHubFilter(initialHubFilter),
  );
  const [hubClientFilter, setHubClientFilter] = useState(initialClientId || "");
  const [hubSortKey, setHubSortKey] = useState(DEFAULT_PROJECT_HUB_SORT);
  const [hubFilterSheetOpen, setHubFilterSheetOpen] = useState(false);
  const [mode, setMode] = useState(() => (editProjectId ? "edit" : "create"));

  const [selectedProjectId, setSelectedProjectId] = useState(
    editProjectId || "",
  );
  const [allProjects, setAllProjects] = useState([]);
  const [projectsFromDevice, setProjectsFromDevice] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [favoriteReady, setFavoriteReady] = useState({});
  const [clients, setClients] = useState([]);

  const [client, setClient] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectNumber, setProjectNumber] = useState("");

  const [siteAddress, setSiteAddress] = useState("");
  const [projectType, setProjectType] = useState("");
  const [status, setStatus] = useState("active");

  const [clientContactName, setClientContactName] = useState("");
  const [clientContactPhone, setClientContactPhone] = useState("");
  const [clientContactEmail, setClientContactEmail] = useState("");
  const [projectManager, setProjectManager] = useState("");

  const [startDate, setStartDate] = useState("");
  const [estimatedEndDate, setEstimatedEndDate] = useState("");

  const [budgetAmount, setBudgetAmount] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");

  const [notes, setNotes] = useState("");
  const PROJECT_DRAFT_KEY = "createProjectDraft";

  useEffect(() => {
    if (!actor) {
      return;
    }

    loadClients();
    loadAllProjects();
  }, [actor]);

  useEffect(() => {
    setHubClientFilter(initialClientId || "");
  }, [initialClientId]);

  useEffect(() => {
    setHubStatusFilter(resolveProjectHubFilter(initialHubFilter));
  }, [initialHubFilter]);

  useEffect(() => {
    if (!editProjectId) {
      return;
    }

    setMode("edit");
    setSelectedProjectId(editProjectId);
    setView("form");
  }, [editProjectId]);

  useEffect(() => {
    if (!isFromEstimate) return;

    setMode("create");
    setSelectedProjectId("");
    setView("form");
    setProjectName(fromEstimate.projectName || "");
    setClient(fromEstimate.clientId || "");
  }, [fromEstimate, isFromEstimate]);

  useEffect(() => {
    if (!sourceEstimateId) return;
    creationSessionRef.current.startNewAttempt();
  }, [sourceEstimateId]);

  useEffect(() => {
    if (!selectedProjectId) return;

    const project = allProjects.find(
      (p) => p.project_id === selectedProjectId,
    );

    if (!project) return;

    setProjectName(project.project_name || "");
    setProjectNumber(project.project_number || "");
    setClient(project.client_id || "");
    setSiteAddress(project.site_address || "");
    setProjectType(project.project_type || "");
    setStatus(project.status || "active");
    setClientContactName(project.client_contact_name || "");
    setProjectManager(project.project_manager || "");
    setStartDate(project.start_date || "");
    setEstimatedEndDate(project.estimated_end_date || "");
    setBudgetAmount(project.budget_amount || "");
    setHourlyRate(project.hourly_rate || "");
    setNotes(project.notes || "");
  }, [selectedProjectId, allProjects]);

  useEffect(() => {
    if (isFromEstimate) return;
    if (view !== "form" || mode !== "create") return;

    const savedDraft = localStorage.getItem(PROJECT_DRAFT_KEY);

    if (!savedDraft) return;

    try {
      const draft = JSON.parse(savedDraft);

      setClient(draft.client || "");
      setProjectName(draft.projectName || "");
      setProjectNumber(draft.projectNumber || "");
      setSiteAddress(draft.siteAddress || "");
      setProjectType(draft.projectType || "");
      setStatus(draft.status || "active");
      setClientContactName(draft.clientContactName || "");
      setClientContactPhone(draft.clientContactPhone || "");
      setClientContactEmail(draft.clientContactEmail || "");
      setProjectManager(draft.projectManager || "");
      setStartDate(draft.startDate || "");
      setEstimatedEndDate(draft.estimatedEndDate || "");
      setBudgetAmount(draft.budgetAmount || "");
      setHourlyRate(draft.hourlyRate || "");
      setNotes(draft.notes || "");
      if (!creationSessionRef.current.adopt(draft.creationIdempotencyKey)) {
        creationSessionRef.current.startNewAttempt();
      }
    } catch (error) {
      console.error("PROJECT DRAFT LOAD ERROR:", error);
    }
  }, [isFromEstimate, view, mode]);

  useEffect(() => {
    if (isFromEstimate) return;
    if (view !== "form" || mode !== "create") return;

    if (!projectName && !projectNumber && !siteAddress && !notes) {
      return;
    }

    localStorage.setItem(
      PROJECT_DRAFT_KEY,
      JSON.stringify({
        client,
        projectName,
        projectNumber,
        siteAddress,
        projectType,
        status,
        clientContactName,
        clientContactPhone,
        clientContactEmail,
        projectManager,
        startDate,
        estimatedEndDate,
        budgetAmount,
        hourlyRate,
        notes,
        creationIdempotencyKey: creationSessionRef.current.peek(),
      }),
    );
  }, [
    isFromEstimate,
    view,
    mode,
    client,
    projectName,
    projectNumber,
    siteAddress,
    projectType,
    status,
    clientContactName,
    clientContactPhone,
    clientContactEmail,
    projectManager,
    startDate,
    estimatedEndDate,
    budgetAmount,
    hourlyRate,
    notes,
  ]);

  useEffect(() => {
    if (mode === "edit") {
      localStorage.removeItem(PROJECT_DRAFT_KEY);
    }
  }, [mode]);

  async function loadClients() {
    if (shouldSkipSupabase(readGuestSession()) || !actor) {
      setClients([]);
      return;
    }

    const {
      data: { user },
    } = await supabase.auth.getUser();

    try {
      const data = await loadScopedClients(
        supabase,
        actor,
        "client_id, client_name",
        {},
        {
          activeOnly: true,
          createdBy: !isAdmin(actor) ? user?.id : null,
        },
      );

      setClients(data);
    } catch (error) {
      console.error("CLIENT LOAD ERROR:", error);
    }
  }

  async function loadAllProjects() {
    if (shouldSkipSupabase(readGuestSession()) || !actor) {
      setAllProjects([]);
      setProjectsFromDevice(false);
      return;
    }

    try {
      const { projects, fromDevice } = await loadFieldProjects(actor, async () => {
        const relation = getProjectsRelationName(actor);
        const projectSelect =
          relation === "projects_operational"
            ? "project_id, project_name, client_id, created_by, notes, organization_id, is_active, status, project_number, site_address, client_name, created_at, updated_at"
            : "*, clients(client_name)";

        return loadScopedActiveProjects(
          supabase,
          actor,
          {},
          projectSelect,
          { activeOnly: false },
        );
      });

      setAllProjects(projects);
      setProjectsFromDevice(fromDevice);
    } catch (error) {
      console.error("PROJECT LOAD ERROR:", error);
      setProjectsFromDevice(false);
    }
  }

  useEffect(() => {
    if (!actor?.supabase_user_id) {
      setFavoriteIds([]);
      setFavoriteReady({});
      return undefined;
    }
    const refreshFavorites = () => {
      setFavoriteIds(readFavoriteProjectIds(actor));
      setFavoriteReady(readFavoriteReadyMap(actor));
    };
    refreshFavorites();
    window.addEventListener("crewpilot-project-favorites", refreshFavorites);
    return () => window.removeEventListener("crewpilot-project-favorites", refreshFavorites);
  }, [actor]);

  async function handleToggleFavorite(projectId) {
    await toggleProjectFavorite(actor, projectId);
  }

  async function handleRetryFavorite(projectId) {
    await retryFavoriteProject(actor, projectId);
  }

  const taskStats = useProjectTaskStats({ enabled: view === "hub" });
  const hubProjects = allProjects.filter((project) =>
    projectMatchesHubFilter(project, hubStatusFilter, {
      overdueProjectIds: taskStats?.overdueProjectIds,
    }),
  );
  const search = searchTerm.toLowerCase().trim();
  const displayedProjects = useMemo(() => {
    const filtered = hubProjects.filter((project) => {
      if (hubClientFilter && project.client_id !== hubClientFilter) {
        return false;
      }
      return projectMatchesSearch(project, search);
    });

    return sortProjectHubList(filtered, hubSortKey);
  }, [hubProjects, search, hubClientFilter, hubSortKey]);
  const hubSections = useMemo(
    () => partitionFavoriteHubProjects(displayedProjects, favoriteIds),
    [displayedProjects, favoriteIds],
  );

  const isDesktopViewport = useIsDesktopViewport();
  const canViewProjectValue = can(CAPABILITY.PROJECT_DETAIL_FINANCIAL);
  const totalProjectValue = useMemo(() => {
    const amounts = getActiveProjects(allProjects)
      .map((project) => (project.budget_amount === null || project.budget_amount === undefined ? NaN : Number(project.budget_amount)))
      .filter(Number.isFinite);
    return amounts.length ? amounts.reduce((sum, value) => sum + value, 0) : null;
  }, [allProjects]);

  const hubClientFilterOptions = useMemo(
    () => [
      { value: "", label: "All Clients" },
      ...clients.map((c) => ({
        value: c.client_id,
        label: c.client_name || "Unnamed Client",
      })),
    ],
    [clients],
  );

  const clientOptions = useMemo(() => {
    const options = clients.map((c) => ({
      value: c.client_id,
      label: c.client_name,
    }));

    if (
      fromEstimate?.clientId &&
      !options.some((option) => option.value === fromEstimate.clientId)
    ) {
      options.unshift({
        value: fromEstimate.clientId,
        label: fromEstimate.clientName || "Estimate client",
      });
    }

    return options;
  }, [clients, fromEstimate?.clientId, fromEstimate?.clientName]);

  const [voiceSessionEnabled, setVoiceSessionEnabled] = useState(false);

  useEffect(() => {
    if (voiceWorkflow?.workflow !== "create_project_mvp") {
      return;
    }

    if (view !== "form" || mode !== "create") {
      onVoiceWorkflowConsumed?.();
      return;
    }

    setVoiceSessionEnabled(true);
    onVoiceWorkflowConsumed?.();
  }, [voiceWorkflow, view, mode, onVoiceWorkflowConsumed]);

  const handleVoiceSessionComplete = useCallback(() => {
    setVoiceSessionEnabled(false);
  }, []);

  async function handleArchiveProject(projectId) {
    if (blockDemoSave()) {
      return;
    }

    if (!window.confirm("Archive this project?")) {
      return;
    }

    const { error } = await archiveProject(supabase, projectId, actor);

    if (error) {
      console.error("[ArchiveProject] Failed:", error);
      alert("Unable to archive project.");
      return;
    }

    notifyProjectArchived(projectId);
    await loadAllProjects();
  }

  async function handleRestoreProject(projectId) {
    if (blockDemoSave()) {
      return;
    }

    if (!window.confirm("Restore this project to Active?")) {
      return;
    }

    const { error } = await restoreProject(supabase, projectId, actor);

    if (error) {
      console.error("[RestoreProject] Failed:", error);
      alert("Unable to restore project.");
      return;
    }

    await loadAllProjects();
  }

  function resetFormFields() {
    setClient("");
    setProjectName("");
    setProjectNumber("");
    setSiteAddress("");
    setProjectType("");
    setStatus("active");
    setClientContactName("");
    setClientContactPhone("");
    setClientContactEmail("");
    setProjectManager("");
    setStartDate("");
    setEstimatedEndDate("");
    setBudgetAmount("");
    setHourlyRate("");
    setNotes("");
  }

  function openCreateForm() {
    setMode("create");
    setSelectedProjectId("");

    if (!localStorage.getItem(PROJECT_DRAFT_KEY)) {
      resetFormFields();
      creationSessionRef.current.startNewAttempt();
    }

    setView("form");
  }

  function openEditForm(projectId) {
    setMode("edit");
    setSelectedProjectId(projectId);
    setView("form");
  }

  function handleCancelForm() {
    if (editProjectId && onEditFinished) {
      onEditFinished();
      return;
    }

    if (isFromEstimate) {
      onBack?.();
      return;
    }

    setView("hub");
  }

  function handleEditFormComplete() {
    setSubmitted(false);
    loadAllProjects();

    if (editProjectId && onEditFinished) {
      onEditFinished();
      return;
    }

    if (isFromEstimate) {
      onBack?.();
      return;
    }

    setView("hub");
  }

  function handleHubProjectSelect(projectId) {
    const project = allProjects.find(
      (entry) => entry.project_id === projectId,
    );

    if (!project) {
      return;
    }

    onOpenProjectDetail?.({
      project_id: project.project_id,
      name: project.project_name,
    });
  }

  function handleSwipeBack() {
    if (view === "form") {
      handleCancelForm();
      return;
    }

    onBack?.();
  }

  useHardwareBackHandler(() => {
    handleSwipeBack();
    return true;
  });

  const carouselEnabled =
    Boolean(onCarouselNavigate) &&
    view === "hub" &&
    !editProjectId &&
    !isFromEstimate;
  const { ref: carouselRef, style: carouselTouchStyle } =
    useDashboardCarouselSwipe({
      onNavigate: onCarouselNavigate,
      onOpenNavigator,
      enabled: carouselEnabled,
    });

  const submitCreateOrEditProject = async () => {
    if (!isUuidString(client)) {
      alert("Select a client before saving.");
      return false;
    }

    if (client) {
      const selectedClient =
        (clients || []).find((row) => row.client_id === client) || {
          client_id: client,
        };
      // Hub client lists are active-only; still assert in case of stale selection.
      if (
        isClientArchived(selectedClient) ||
        isClientOperationallyLocked(selectedClient)
      ) {
        alert(OPERATIONAL_LOCK_CLIENT_MESSAGE);
        return false;
      }
      try {
        assertClientOperationallyWritable(selectedClient);
      } catch (lockError) {
        alert(lockError?.message || OPERATIONAL_LOCK_CLIENT_MESSAGE);
        return false;
      }
    }

    const {
      data: { user },
    } = await supabase.auth.getUser();

    const persistedNotes = buildProjectNotes(
      notes,
      clientContactPhone,
      clientContactEmail,
    );

    if (mode === "edit") {
      try {
        const updateData = {
          client_id: client,
          project_name: projectName,
          project_number: projectNumber,
          site_address: siteAddress,
          project_type: projectType,
          status: status,
          client_contact_name: clientContactName,
          project_manager: projectManager,
          start_date: startDate || null,
          estimated_end_date: estimatedEndDate || null,
          budget_amount: !String(budgetAmount || "").trim()
            ? null
            : Number(budgetAmount),
          hourly_rate: !String(hourlyRate || "").trim()
            ? null
            : Number(hourlyRate),
          notes: persistedNotes,
        };

        const { error } = await updateScopedProject(
          supabase,
          actor || currentUser,
          selectedProjectId,
          updateData,
        );

        if (error) throw error;

        setSubmitted(true);
        return true;
      } catch (error) {
        logProjectMutationFailure("UpdateProject", error);
        alert("Unable to update project.");
        return false;
      }
    }

    try {
      const insertPayload = {
        client_id: client,
        project_name: projectName,
        project_number: projectNumber,
        site_address: siteAddress,
        project_type: projectType,
        status: status,
        client_contact_name: clientContactName,
        project_manager: projectManager,
        start_date: startDate || null,
        estimated_end_date: estimatedEndDate || null,
        budget_amount: !String(budgetAmount || "").trim()
          ? null
          : Number(budgetAmount),
        hourly_rate: !String(hourlyRate || "").trim()
          ? null
          : Number(hourlyRate),
        notes: persistedNotes,
        created_by: user.id,
        [PROJECT_CREATION_IDEMPOTENCY_COLUMN]: creationSessionRef.current.token(),
      };

      if (fromEstimate?.estimateId) {
        insertPayload.source_estimate_id = fromEstimate.estimateId;
      }

      const { data: createdProject, error } = await insertScopedProject(
        supabase,
        currentUser,
        insertPayload,
      );

      if (error) throw error;

      if (fromEstimate?.estimateId && createdProject?.project_id) {
        try {
          await ensureProjectQuoteSnapshotsFromEstimate({
            actor: currentUser,
            projectId: createdProject.project_id,
            estimateId: fromEstimate.estimateId,
            userId: user?.id,
          });
          await copyEstimateStructureToProject({
            actor: currentUser,
            projectId: createdProject.project_id,
            estimateId: fromEstimate.estimateId,
          });
          await markEstimateConverted(
            currentUser,
            fromEstimate.estimateId,
            createdProject.project_id,
            user?.id,
          );
        } catch (conversionError) {
          console.error("[CreateProject] Estimate conversion failed:", conversionError);
          alert(
            "Project was created, but quote snapshots, locations, or estimate conversion could not be completed. Please refresh and contact support if this persists.",
          );
        }
      }

      localStorage.removeItem(PROJECT_DRAFT_KEY);
      if (createdProject?.project_id && onOpenProjectDetail) {
        onOpenProjectDetail({
          project_id: createdProject.project_id,
          name: createdProject.project_name || projectName,
        });
        return true;
      }
      setSubmitted(true);
      return true;
    } catch (error) {
      logProjectMutationFailure("CreateProject", error);
      if (classifyProjectUniqueViolation(error) === PROJECT_SOURCE_ESTIMATE_COLUMN) {
        alert("This estimate is already linked to a project.");
      } else {
        alert("Unable to create project.");
      }
      return false;
    }
  };

  /**
   * One shared lock for the Save button and Ashley/voice onSaveProject.
   * Acquired synchronously, before the first await, so a second call cannot insert.
   */
  const handleSubmit = async () => {
    if (blockDemoSave()) {
      return false;
    }

    if (!submissionLockRef.current.tryAcquire()) {
      return false;
    }

    setIsSubmitting(true);
    try {
      return await submitCreateOrEditProject();
    } finally {
      submissionLockRef.current.release();
      setIsSubmitting(false);
    }
  };

  const voiceMvp = useCreateProjectVoiceMvp({
    enabled: voiceSessionEnabled,
    setClient,
    clientOptions,
    setProjectName,
    setClientContactName,
    setClientContactPhone,
    setClientContactEmail,
    setNotes,
    onSaveProject: handleSubmit,
    onComplete: handleVoiceSessionComplete,
  });

  if (submitted) {
    return (
      <div className="screen nx-screen ci-success-screen">
        <div className="ci-success-content">
          <div className="ci-success-icon">
            <svg
              viewBox="0 0 52 52"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="checkmark-circle nx-checkmark-circle"
                cx="26"
                cy="26"
                r="24"
              />
              <path
                className="checkmark-check  nx-checkmark-check"
                d="M14.1 27.2l7.1 7.2 16.7-16.8"
              />
            </svg>
          </div>
          <h1 className="ci-success-title">
            {mode === "edit" ? "Project Updated" : "Project Created"}
          </h1>
          <p className="ci-success-sub">
            {projectName || "Project"} has been{" "}
            {mode === "edit" ? "updated" : "added"}.
          </p>
          <button
            className="nx-btn nx-btn-gold"
            style={{ marginTop: "12px" }}
            onClick={handleEditFormComplete}
          >
            {editProjectId
              ? "Back to Project"
              : isFromEstimate
                ? "Back to Estimate"
                : "Back to Projects"}
          </button>
        </div>
      </div>
    );
  }

  const swipeHandlers = {
    onTouchStart: (e) => {
      if (e.touches.length !== 1) return;

      touchStartX.current = e.touches[0].clientX;
    },
    onTouchEnd: (e) => {
      if (touchStartX.current === null) return;

      if (e.changedTouches.length !== 1) {
        touchStartX.current = null;
        return;
      }

      const deltaX = e.changedTouches[0].clientX - touchStartX.current;

      if (Math.abs(deltaX) > 80) {
        handleSwipeBack();
      }

      touchStartX.current = null;
    },
  };

  if (view === "hub") {
    const nameSortOptions = PROJECT_HUB_SORT_OPTIONS.filter((option) =>
      option.value.startsWith("name_"),
    );
    const clientFilterName = hubClientFilter
      ? clients.find((c) => c.client_id === hubClientFilter)?.client_name || "Client"
      : "";
    const sortChanged = hubSortKey !== DEFAULT_PROJECT_HUB_SORT;
    const hubChips = [
      ...(hubClientFilter
        ? [{ key: "client", label: clientFilterName, onClear: () => setHubClientFilter("") }]
        : []),
      ...(sortChanged
        ? [
            {
              key: "sort",
              label: nameSortOptions.find((o) => o.value === hubSortKey)?.label || "Sorted",
              onClear: () => setHubSortKey(DEFAULT_PROJECT_HUB_SORT),
            },
          ]
        : []),
    ];
    const hubFilterFields = [
      {
        key: "client",
        label: "Client",
        value: hubClientFilter,
        onChange: setHubClientFilter,
        options: hubClientFilterOptions,
      },
      {
        key: "sort",
        label: "Sort",
        value: hubSortKey,
        onChange: setHubSortKey,
        options: nameSortOptions,
      },
    ];
    const clearHubFilters = () => {
      setHubClientFilter("");
      setHubSortKey(DEFAULT_PROJECT_HUB_SORT);
    };
    const renderCard = (project, favorite) => (
      <ProjectCard
        key={project.project_id}
        project={project}
        onSelect={handleHubProjectSelect}
        onArchive={handleArchiveProject}
        onRestore={handleRestoreProject}
        favorite={favorite}
        favoriteState={favorite ? favoriteReady[project.project_id] || "" : ""}
        onToggleFavorite={handleToggleFavorite}
        onRetryFavorite={handleRetryFavorite}
        taskStats={taskStats ? taskStats.byProject.get(project.project_id) || null : null}
        valueLabel={
          canViewProjectValue && Number.isFinite(Number(project.budget_amount)) && project.budget_amount !== null
            ? formatProjectValue(Number(project.budget_amount))
            : ""
        }
      />
    );

    return (
      <div
        ref={carouselRef}
        style={carouselTouchStyle}
        {...(carouselEnabled ? {} : swipeHandlers)}
      >
        <CrewPilotModuleShell
          title="Projects"
          subtitle="Project Management"
          onBack={onBack}
          layout={PAGE_LAYOUT.COMMAND}
          primary
          compact
        >
          <div className="pj-hub-controls">
            <TimeLogsScopeTabs
              ariaLabel="Project status filters"
              options={PROJECT_HUB_STATUS_FILTERS}
              value={hubStatusFilter}
              onChange={setHubStatusFilter}
            />

            <TimeLogsSearchRow
              search={searchTerm}
              onSearch={setSearchTerm}
              filterCount={hubChips.length}
              onOpenFilters={() => setHubFilterSheetOpen(true)}
              placeholder="Search projects..."
              searchLabel="Search projects"
            />

            <TimeLogsActiveFilters chips={hubChips} onClearAll={clearHubFilters} />

            <TimeLogsActionBar
              summary={`${displayedProjects.length} ${
                displayedProjects.length === 1 ? "project" : "projects"
              }${hubClientFilter ? " for client" : ""}`}
              onNew={can(CAPABILITY.PROJECT_CREATE) ? openCreateForm : null}
              newLabel="New Project"
              newAriaLabel="+ New Project"
            />
          </div>

          <DesktopKpiStrip
            ariaLabel="Projects summary"
            tiles={[
              {
                key: "active",
                value: getActiveProjects(allProjects).length,
                label: "Active Projects",
                tone: "gold",
              },
              {
                key: "overdue",
                value: taskStats ? taskStats.totals.overdue : "—",
                label: "Tasks Overdue",
                tone: "red",
              },
              {
                key: "week",
                value: taskStats ? taskStats.totals.dueThisWeek : "—",
                label: "Tasks Due This Week",
                tone: "blue",
              },
              canViewProjectValue
                ? {
                    key: "value",
                    value: totalProjectValue == null ? "—" : formatProjectValue(totalProjectValue),
                    label: "Total Project Value",
                    tone: "green",
                  }
                : null,
            ]}
          />

          {projectsFromDevice ? (
            <p
              data-testid="project-hub-device-copy"
              style={{
                margin: "0 0 12px",
                color: "var(--nx-text-muted, #8a95a8)",
                fontSize: "13px",
                lineHeight: 1.4,
              }}
            >
              Saved on this device from the last time these projects were opened.
            </p>
          ) : null}

          <div className="cp-page-data-surface">
            {displayedProjects.length === 0 ? (
              <p
                style={{
                  textAlign: "center",
                  marginTop: "24px",
                  color: "var(--nx-text-muted, #5b6472)",
                }}
              >
                {search || hubClientFilter
                  ? "No projects match your filters."
                  : hubStatusFilter === "archived"
                    ? "No archived projects found."
                    : hubStatusFilter === PROJECT_HUB_OVERDUE_TASKS
                      ? "No projects with overdue tasks."
                      : "No projects found."}
              </p>
            ) : (
              <>
                {hubSections.favorites.length > 0 && (
                  <div data-testid="project-hub-favorites">
                    <p className="pj-section-label pj-section-label--fav">★ FAVORITES</p>
                    {hubSections.favorites.map((project) => renderCard(project, true))}
                  </div>
                )}
                {hubSections.rest.length > 0 && (
                  <div data-testid="project-hub-all">
                    {hubSections.favorites.length > 0 && (
                      <p className="pj-section-label">ALL PROJECTS</p>
                    )}
                    {hubSections.rest.map((project) => renderCard(project, false))}
                  </div>
                )}
              </>
            )}
          </div>
        </CrewPilotModuleShell>

        {hubFilterSheetOpen ? (
          <TimeLogsFilterSheet
            title="Filter projects"
            fields={hubFilterFields}
            hasActive={hubChips.length > 0}
            onClearAll={clearHubFilters}
            onClose={() => setHubFilterSheetOpen(false)}
          />
        ) : null}
      </div>
    );
  }

  return (
    <VoiceGuidedFormProvider value={voiceMvp}>
      <div {...swipeHandlers}>
        <div>
          <CreateProjectFormFields
            title={mode === "edit" ? "Edit Project" : "Create Project"}
            subtitle={
              isFromEstimate
                ? "Linked to estimate — complete project details and save"
                : mode === "edit"
                  ? "Update project details"
                  : "Add a new project"
            }
            isEditing={mode === "edit"}
            projectName={projectName}
            setProjectName={setProjectName}
            projectNumber={projectNumber}
            setProjectNumber={setProjectNumber}
            client={client}
            setClient={setClient}
            clientOptions={clientOptions}
            clientDropdownRef={clientDropdownRef}
            siteAddress={siteAddress}
            setSiteAddress={setSiteAddress}
            projectType={projectType}
            setProjectType={setProjectType}
            clientContactName={clientContactName}
            setClientContactName={setClientContactName}
            clientContactPhone={clientContactPhone}
            setClientContactPhone={setClientContactPhone}
            clientContactEmail={clientContactEmail}
            setClientContactEmail={setClientContactEmail}
            startDate={startDate}
            setStartDate={setStartDate}
            estimatedEndDate={estimatedEndDate}
            setEstimatedEndDate={setEstimatedEndDate}
            projectManager={projectManager}
            setProjectManager={setProjectManager}
            status={status}
            setStatus={setStatus}
            budgetAmount={budgetAmount}
            setBudgetAmount={setBudgetAmount}
            hourlyRate={hourlyRate}
            setHourlyRate={setHourlyRate}
            notes={notes}
            setNotes={setNotes}
            onCancel={handleCancelForm}
            onSubmit={handleSubmit}
            submitting={isSubmitting}
          />
        </div>

        <VoiceGuidedFormOverlay
          isActive={voiceMvp.isActive}
          phase={voiceMvp.phase}
          statusMessage={voiceMvp.statusMessage}
          interactionUI={voiceMvp.interactionUI}
          onConfirmYes={voiceMvp.confirmStepYes}
          onConfirmChooseAgain={voiceMvp.confirmStepNo}
        />
      </div>
    </VoiceGuidedFormProvider>
  );
}
